import { useOne, useParse, useTranslation } from "@refinedev/core";
import {
  CreateEventStep,
  IAdditionalSetupData,
  ISimpleEventDetail,
  UserType,
} from "interfaces";
import { BasicInformation } from "./basicInformation";
import Button from "components/button";
import { Link } from "react-router-dom";
import { ChangeRegistrationInformation } from "./changeRegistrationInformation";
import { ChangeDistance } from "./changeDistance";
import { TransferTicket } from "./transferTicket";
import { PaymentMethod } from "./paymentMethod";
import { SwitchButton } from "components/switchButton";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PrivateFlow } from "./privateFlow";
import { CollapsibleSection } from "components/collapsibleSection";
import { endpointService } from "components/shared/services/endpointService";
import { Label } from "components/label";
import { canAccessByType } from "utills/helpers";
import { LegalInfo } from "./legalInfo";
import { Einvoice } from "./einvoice";

export const ViewAdditionalSetup = ({
  eventDetailData,
}: {
  eventDetailData: ISimpleEventDetail;
}) => {
  const { translate } = useTranslation();
  const parse = useParse();
  const { params } = parse();

  const { data, isFetching } = useOne<IAdditionalSetupData>({
    resource: endpointService.getEventEndpoint("getAdditionalSetupData", {
      eventId: params?.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(
        params?.id &&
        params?.step &&
        eventDetailData?.current_step >= params?.step
      ),
    } as any,
  });

  const additionalSetupData = data?.data ?? null;

  if (isFetching) {
    return <FullScreenLoading />;
  }

  return (
    <div className="flex flex-col gap-y-4 h-full sm:gap-y-6">
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.basicInformation")}
        defaultOpen={false}
      >
        <BasicInformation
          eventDetailData={eventDetailData}
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate(
          "eventSetup.additionalSetup.section.changeRegistrationInformation"
        )}
        customSubLabel={
          <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
            <Label
              id="is_change_registration"
              className="m-0 font-normal"
              name={translate(
                "eventSetup.additionalSetup.changeRegistrationInformation.acceptEditRegistrationInfo"
              )}
            />
            <SwitchButton
              checked={additionalSetupData?.is_change_registration ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        }
        defaultOpen={false}
        hideArrow={!(additionalSetupData?.is_change_registration ?? false)}
        hideContent={!(additionalSetupData?.is_change_registration ?? false)}
      >
        <ChangeRegistrationInformation
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.changeDistance")}
        customSubLabel={
          <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
            <Label
              id="is_change_distance"
              className="m-0 font-normal"
              name={translate(
                "eventSetup.additionalSetup.changeDistance.acceptUpgradesOrDowngrades"
              )}
            />
            <SwitchButton
              checked={additionalSetupData?.is_change_distance ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        }
        defaultOpen={false}
        hideArrow={!(additionalSetupData?.is_change_distance ?? false)}
        hideContent={!(additionalSetupData?.is_change_distance ?? false)}
      >
        <ChangeDistance
          eventDetailData={eventDetailData}
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.transferTicket")}
        customSubLabel={
          <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
            <Label
              id="is_transfer_ticket"
              className="m-0 font-normal"
              name={translate(
                "eventSetup.additionalSetup.transferTicket.acceptTransfer"
              )}
            />
            <SwitchButton
              checked={additionalSetupData?.is_transfer_ticket ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        }
        defaultOpen={false}
        hideArrow={!(additionalSetupData?.is_transfer_ticket ?? false)}
        hideContent={!(additionalSetupData?.is_transfer_ticket ?? false)}
      >
        <TransferTicket
          eventDetailData={eventDetailData}
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.privateFlow")}
        customSubLabel={
          <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
            <Label
              id="private_flow_is_use"
              className="m-0 font-normal"
              name={translate(
                "eventSetup.additionalSetup.privateFlow.usePrivateFlow"
              )}
            />
            <SwitchButton
              checked={additionalSetupData?.private_flow?.is_use ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        }
        defaultOpen={false}
        hideArrow={!(additionalSetupData?.private_flow?.is_use ?? false)}
        hideContent={!(additionalSetupData?.private_flow?.is_use ?? false)}
      >
        <PrivateFlow
          eventDetailData={eventDetailData}
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.paymentMethod")}
        defaultOpen={false}
      >
        <PaymentMethod additionalSetupData={additionalSetupData} />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.additionalSetup.section.einvoice")}
        customSubLabel={
          <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
            <Label
              id="private_flow_is_use"
              className="m-0 font-normal"
              name={translate(
                "eventSetup.additionalSetup.einvoice.isEinvoiceEnabled"
              )}
            />
            <SwitchButton
              checked={additionalSetupData?.is_einvoice_enabled ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        }
        defaultOpen={false}
        hideArrow={!(additionalSetupData?.is_einvoice_enabled ?? false)}
        hideContent={!(additionalSetupData?.is_einvoice_enabled ?? false)}
      >
        <Einvoice
          eventDetailData={eventDetailData}
          additionalSetupData={additionalSetupData}
        />
      </CollapsibleSection>
      {canAccessByType(UserType.ADMIN) && (
        <CollapsibleSection
          label="Legal Info"
          defaultOpen={false}
        >
          <LegalInfo additionalSetupData={additionalSetupData} />
        </CollapsibleSection>
      )}
      <div
        className="mt-auto w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
        style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
      >
        <div className="flex items-center justify-end gap-x-4">
          <Link
            to={`/events/sports/${params?.id}?step=${CreateEventStep.CREATE_TICKET_PHASE}`}
          >
            <Button className="min-w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
              {translate("button.back")}
            </Button>
          </Link>
          <Link to={`/events/sports`}>
            <Button className="w-fit min-w-20" type="submit">
              {translate("button.backToList")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
